import * as ZapparThree from "@zappar/zappar-threejs";
import { Clock, DataTexture, Matrix4, PerspectiveCamera, Scene, sRGBEncoding } from "three";
import config from "../utils/config";
import Renderer from "./Renderer";
import World from "./World";
import { awaitTimer, getMobileOS } from "./utils";
import DeviceOrientationControls from "./utils/DeviceOrientationControls";
import { loadGltfs, loadRgbe, loadTextures } from "./utils/loadAssets";
import { LoadedGLTFs, LoadedTextures } from "./utils/types";
import { Params } from "../utils/types";
import { hidePermissions, revealPermissionsLoader, revealPrompt } from "../dom";

const isOrientationControls = (
    controls: DeviceOrientationControls
): controls is DeviceOrientationControls => controls instanceof DeviceOrientationControls;


export default class Experience {
    private targetElement: HTMLDivElement;
    private config!: typeof config;
    private scene!: Scene;
    private renderer!: Renderer;
    private perspectiveCamera!: THREE.PerspectiveCamera;
    private zapparCamera!: ZapparThree.Camera;
    private zapparCameraMatrix!: THREE.Matrix4;
    private zapparCameraPlaying!: boolean;
    private controls!: DeviceOrientationControls;
    private isDeviceOrientationControls!: boolean;
    private firstTick = false;

    public world!: World;

    private lastElapsedTime = 0;
    private clock = new Clock();
    private gltfs!: LoadedGLTFs;
    private textures!: LoadedTextures;
    private declare envMap: DataTexture;
    public urlParams!: Params;

    private readonly deviceType = getMobileOS();


    constructor(options: { targetElement: HTMLDivElement }) {

        this.targetElement = options.targetElement;


        if (ZapparThree.browserIncompatible()) {
            ZapparThree.browserIncompatibleUI();
            throw new Error("Unsupported browser");
        }

        if (!options?.targetElement) {
            console.warn("Missing 'targetElement' property");
            return;
        }

        this.perspectiveCamera = new PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);

        this.zapparCameraPlaying = false;
        this.perspectiveCamera.position.z = 0;
        this.controls = new DeviceOrientationControls({ camera: this.perspectiveCamera, isAndroid: this.deviceType === "Android", });

        this.perspectiveCamera.updateWorldMatrix(true, true);

        this.setConfig();
        this.setRenderer();
        this.setScenes();
        this.update();

        // The Zappar library needs the WebGL context to process camera images
        // Use this function to set your context
        ZapparThree.glContextSet(this.renderer.instance.getContext());

        // Create a camera and set the scene background to the camera's backgroundTexture
        this.zapparCamera = new ZapparThree.Camera();
        this.zapparCamera.backgroundTexture.encoding = sRGBEncoding;
        this.zapparCameraMatrix = new Matrix4();

    }

    start = () => {

        if (isOrientationControls(this.controls)) this.controls.connect();
        this.zapparCamera.start(false)
        this.zapparCameraPlaying = true;

        revealPermissionsLoader();
        awaitTimer(2000).then(() => {
            hidePermissions();
            awaitTimer(500).then(() => {
                this.setWorld();
                this.isDeviceOrientationControls = isOrientationControls(this.controls);
            })
        })
        //Temporary fix to Android black screen bug
        awaitTimer(500).then(() => {
            this.zapparCamera.start();
            awaitTimer(500).then(() => {
                this.zapparCamera.start();
            })
        })
    }

    init = (urlParams: Params) => {
        this.urlParams = urlParams;
        this.loadAssets()

    };

    loadAssets = async () =>
        await loadGltfs(this.urlParams).then(gltfs => {
            this.gltfs = gltfs
            loadTextures().then(textures => {
                this.textures = textures;
                this.loadRgbe()
            })
        })


    loadRgbe = async () => {
        await loadRgbe(this.scene).then((envMap: DataTexture) => {
            console.log('RGBE TEXTURE LOADED', envMap)
            this.envMap = envMap;
        })

    };

    private onFirstTick = () => {
        this.world.pivot.rotatePivotToCamera();
        this.firstTick = true;
    };

    private setWorld = async () => {
        this.world = new World(
            this.scene,
            this.zapparCamera,
            this.perspectiveCamera,
            this.gltfs,
            this.textures,
            this.envMap,
            this.urlParams,
            this.renderer,
        );
    };

    private setConfig = () => {
        this.config = config;
    };

    private setScenes = () => {
        this.scene = new Scene();
    };

    private setRenderer = () => {
        this.renderer = new Renderer();
        this.targetElement.appendChild(this.renderer.instance.domElement);
        ZapparThree.glContextSet(this.renderer.instance.getContext());
    };

    private update = () => {
        const elapsedTime = this.clock.getElapsedTime();
        const deltaTime = elapsedTime - this.lastElapsedTime;
        this.lastElapsedTime = elapsedTime;


        this.world?.update(deltaTime);

        this.isDeviceOrientationControls && (this.controls as DeviceOrientationControls).update();

        if (this.zapparCameraPlaying) {
            this.zapparCamera.updateFrame(this.renderer.instance)
            let texture = this.zapparCamera.backgroundTexture;
            this.perspectiveCamera.updateWorldMatrix(true, true);


            if (this.world != null) this.firstTick || this.onFirstTick();

            this.renderer.update(this.scene, this.zapparCamera, this.perspectiveCamera, texture);
        }

        requestAnimationFrame(this.update);
    };

    public triggerDrawControl = (trigger: string) => {
        this.world.triggerDrawControl(trigger);
        if (trigger == 'penSelection') {
            awaitTimer(1000).then(() => {
                if (isOrientationControls(this.controls)) this.controls.disconnect();
            })
        }
        else if (trigger == "continue") {
            if (isOrientationControls(this.controls)) this.controls.connect();
        }
    }

    public updatePhoneDirection = (phoneDown: boolean) => {
        this.world?.updatePhoneDirection(phoneDown)
    }
}
